<template>
  <div>
    <loading-overlay v-if="updatingClientEvents" :text="''" />
    
    <!-- Permission Check: Nur für echte Admins -->
    <v-container fluid class="px-6 py-6" v-if="!hasAdministrationPermission">
      <permission-denied 
        message="Du benötigst Administrator-Rechte, um auf den Administrationsbereich zuzugreifen."
      />
    </v-container>
    
    <v-container fluid class="px-6 py-6" v-else-if="selectedClientEvent">
      <v-row class="mb-5">
        <v-col cols="12">
          <!-- Archived Badge -->
          <v-alert
            v-if="selectedClientEvent.archived"
            type="warning"
            dense
            colored-border
            border="left"
            elevation="2"
            class="mb-3"
          >
            <div class="d-flex align-center">
              <i class="material-icons-round me-2" style="font-size: 20px; color: #fb6340;">archive</i>
              <div>
                <p class="mb-0 font-weight-600" style="color: #344767;">Event ist archiviert</p>
                <p class="mb-0 text-caption" style="color: #67748e;">
                  Alle Bearbeitungsfunktionen sind deaktiviert.
                </p>
              </div>
            </div>
          </v-alert>

          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-primary py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#5e72e4"
            small
            :disabled="selectedClientEvent.archived"
            @click="
              $router
                .push(
                  '/admin/administration/events/edit/' + selectedClientEvent._id
                )
                .catch(() => {})
            "
            >Event bearbeiten</v-btn
          >
          
          <!-- Löschen Button (nur wenn Event löschbar ist) -->
          <v-btn
            v-if="!selectedClientEvent.archived && canManageEvents && isEventDeletable"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-error py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#f5365c"
            small
            @click="deleteEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">delete</i>
            Event löschen
          </v-btn>
          
          <!-- Archivieren / Dearchivieren Button -->
          <v-btn
            v-if="!selectedClientEvent.archived && canManageEvents && !isEventDeletable"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#fb6340"
            small
            @click="archiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Event archivieren
          </v-btn>
          
          <v-btn
            v-if="selectedClientEvent.archived && canManageEvents"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#2dce89"
            small
            @click="unarchiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Event dearchivieren
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="m-0">
        <v-col cols="12" class="pt-0 mb-10 mt-5">
          <v-card class="mb-30 card card-shadow border-radius-xl py-5">
            <div class="mt-n11 mx-4 card-header position-relative z-index-2">
              <div class="d-block blur-shadow-image">
                <img
                  v-if="selectedClientEvent.img && !imageError"
                  :src="getImage(selectedClientEvent.img)"
                  class="shadow border-radius-lg w-100"
                  style="height: 400px; object-fit: cover;"
                  :alt="selectedClientEvent.img"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="d-flex align-items-center justify-content-center border-radius-lg shadow"
                  style="
                    height: 400px;
                    background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                  "
                >
                  <i class="material-icons-round text-white" style="font-size: 120px; opacity: 0.5;">
                    image_not_supported
                  </i>
                </div>
              </div>
              <div
                v-if="selectedClientEvent.img && !imageError"
                class="colored-shadow"
                v-bind:style="{
                  backgroundImage:
                    'url(' + getImage(selectedClientEvent.img) + ')',
                }"
              ></div>
            </div>
            <div class="card-header-padding">
              <v-row>
                <v-col>
                  <p class="font-weight-600 text-h3 text-typo mb-0">
                    {{ selectedClientEvent.title }} -
                    {{ selectedClientEvent.date | moment("DD.MM.YYYY HH:mm") }}
                    Uhr
                  </p>
                </v-col>
              </v-row>
            </div>
          </v-card>
          <v-row>
            <v-col>
              <v-card
                class="px-4 py-4 card-shadow border-radius-xl h-100"
                :class="
                  selectedClientEvent.approved
                    ? selectedClientEvent.active
                      ? 'bg-gradient-success'
                      : 'bg-gradient-secondary'
                    : 'bg-gradient-warning'
                "
              >
                <p
                  v-if="!selectedClientEvent.approved"
                  class="mb-4 mb-0 text-white font-weight-bold"
                >
                  Eventfreigabe durch Admin offen!
                </p>
                <div class="d-flex mb-4">
                  <p class="mb-0 text-white font-weight-light">
                    {{ selectedClientEvent.active ? " Aktiv" : "Inaktiv" }}
                  </p>
                  <v-switch
                    @change="updateEvent(selectedClientEvent)"
                    :ripple="false"
                    class="d-inline-flex mt-0 pt-0 switch ms-auto"
                    v-model="selectedClientEvent.active"
                    :disabled="!selectedClientEvent.approved"
                    hide-details
                  ></v-switch>
                </div>

                <p class="mt-4 mb-0 text-white font-weight-bold">Eventstatus</p>
                <span class="text-xs text-white">{{
                  selectedClientEvent.active
                    ? "Event ist sichtbar"
                    : "Event ist nicht sichtbar"
                }}</span>
              </v-card>
            </v-col>
            <v-col>
              <v-card
                class="px-4 py-4 card-shadow border-radius-xl h-100"
                :class="
                  selectedClientEvent.approved
                    ? selectedClientEvent.entries_active
                      ? 'bg-gradient-success'
                      : 'bg-gradient-secondary'
                    : 'bg-gradient-warning'
                "
              >
                <p
                  v-if="!selectedClientEvent.approved"
                  class="mb-4 mb-0 text-white font-weight-bold"
                >
                  Eventfreigabe durch Admin offen!
                </p>
                <div class="d-flex mb-4">
                  <p class="mb-0 text-white font-weight-light">
                    {{
                      selectedClientEvent.entries_active ? "Aktiv" : "Inaktiv"
                    }}
                  </p>
                  <v-switch
                    @change="updateEvent(selectedClientEvent)"
                    :ripple="false"
                    class="d-inline-flex mt-0 pt-0 switch ms-auto"
                    v-model="selectedClientEvent.entries_active"
                    :disabled="!selectedClientEvent.approved"
                    hide-details
                  ></v-switch>
                </div>

                <p class="mt-4 mb-0 text-white font-weight-bold">
                  Eventanmeldungen
                </p>
                <span class="text-xs text-white">{{
                  selectedClientEvent.entries_active
                    ? "Anmeldungen sind möglich"
                    : "Anmeldungen sind nicht möglich"
                }}</span>
              </v-card>
            </v-col>
            <v-col sm="3" cols="12">
              <v-card
                class="mb-6 card-shadow border-radius-xl py-4 h-100 w-100"
              >
                <v-row no-gutters class="px-4">
                  <v-col sm="4">
                    <v-avatar
                      color="bg-gradient-primary border-radius-xl mt-n8"
                      class="shadow-primary"
                      height="64"
                      width="64"
                    >
                      <v-icon class="material-icons-round text-white" size="24"
                        >leaderboard</v-icon
                      >
                    </v-avatar>
                  </v-col>
                  <v-col sm="8" class="text-end">
                    <p
                      class="text-sm mb-0 text-capitalize text-body font-weight-light"
                    >
                      Beitrags Interaktionen
                    </p>
                    <h4 class="text-h4 text-typo font-weight-bolder mb-0">
                      {{ selectedClientEvent.views }}
                    </h4>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col sm="3" cols="12">
              <v-card
                class="mb-6 card-shadow border-radius-xl py-4 h-100 w-100"
              >
                <v-row no-gutters class="px-4">
                  <v-col sm="4">
                    <v-avatar
                      color="bg-gradient-info border-radius-xl mt-n8"
                      class="shadow-info"
                      height="64"
                      width="64"
                    >
                      <v-icon class="material-icons-round text-white" size="24"
                        >comments</v-icon
                      >
                    </v-avatar>
                  </v-col>
                  <v-col sm="8" class="text-end">
                    <p
                      class="text-sm mb-0 text-capitalize text-body font-weight-light"
                    >
                      Kommentare
                    </p>
                    <h4 class="text-h4 text-typo font-weight-bolder mb-0">
                      {{ eventComments.length }}
                    </h4>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <datatable-push-notifications></datatable-push-notifications>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <datatable-search></datatable-search>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <datatable-invoices></datatable-invoices>
            </v-col>
          </v-row>

          <!-- <v-card class="card-shadow border-radius-xl mt-5">
            <v-card-text class="card-padding">
              <v-row class="mt-0 ml-1" justify="space-between">
                <h5 class="font-weight-bolder text-h5 text-typo mb-10">
                  {{ eventComments.length }} Kommentare
                </h5>
                <div class="mr-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        @click="getEventComments(selectedClientEvent._id)"
                        v-bind="attrs"
                        v-on="on"
                        class="material-icons-round text-primary ms-auto"
                        size="30"
                      >
                        refresh
                      </v-icon>
                    </template>
                    <span>Aktualisieren</span>
                  </v-tooltip>
                </div>
              </v-row>

              <div v-if="eventComments.length == 0">
                <h5 class="text-h5 text-typo font-weight-bold mt-0 mb-2">
                  Keine Kommentare vorhanden
                </h5>
              </div>
              <div v-else>
                <div class="d-flex">
                  <div class="flex-grow-1 ms-4">
                    <div :key="index" v-for="(comment, index) in eventComments">
                      <v-list-item
                        :ripple="false"
                        class="px-0 border-radius-sm mb-2"
                      >
                        <v-avatar
                          width="48"
                          height="48"
                          class="shadow border-radius-lg me-4"
                        >
                          <img
                            v-if="comment.user.avatar"
                            :src="getImage(comment.user.avatar)"
                            alt="Avatar"
                            class="border-radius-lg"
                          />
                          <div v-else>
                            {{ comment.user.username.charAt(0)
                            }}{{ comment.user.username.charAt(1) }}
                          </div>
                        </v-avatar>
                        <v-list-item-content>
                          <div class="d-flex align-center">
                            <div>
                              <h6
                                class="mb-0 text-sm text-sm text-typo font-weight-bold"
                              >
                                {{ comment.user.username }}
                              </h6>
                              <p
                                class="mb-0 text-xs text-body font-weight-light"
                              >
                                {{ comment.comment }}
                              </p>
                            </div>
                            <div class="ms-auto">
                              <v-btn
                                @click="showDeleteDialog(comment)"
                                :ripple="false"
                                small
                                text
                                width="auto"
                                class="pe-4 ps-0 mb-0 text-primary font-weight-bolder"
                              >
                                Löschen
                              </v-btn>
                            </div>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                      <p class="text-xs mt-2 font-weight-light text-body">
                        {{ comment.created | moment("DD.MM.YYYY HH:mm") }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card> -->
        </v-col>
      </v-row>

      <v-snackbar
        top
        v-model="snackbar.visible"
        :color="snackbar.color"
        class="snackbar-shadow"
      >
        <div class="d-flex align-start alert-notify">
          <v-icon size="24" class="text-white me-3 mt-1 material-icons-round"
            >notifications</v-icon
          >
          <p class="mb-0">
            <span class="font-size-root font-weight-600">{{
              snackbar.title
            }}</span>
            <br />
            {{ snackbar.message }}
          </p>
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn
            icon
            elevation="0"
            max-width="136"
            :ripple="false"
            height="43"
            class="font-weight-600 text-capitalize py-3 px-6 rounded-sm"
            color="rgba(255,255,255, .85)"
            @click="snackbar.visible = false"
            v-bind="attrs"
          >
            <v-icon size="13">fas fa-times</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
    <delete-dialog
      :open="dialogDelete"
      @close="closeCommentDelete"
      @confirm="deleteCommentConfirm"
    ></delete-dialog>

    <!-- Archive Confirmation Dialog -->
    <v-dialog v-model="dialogArchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-warning me-2" style="font-size: 28px;">archive</i>
          Event archivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Sind Sie sicher, dass Sie dieses Event archivieren möchten?
          </p>
          
          <v-alert type="warning" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Das Event wird archiviert und automatisch deaktiviert:</p>
            <ul class="pl-4 text-typo">
              <li>Keine neuen Anmeldungen mehr möglich</li>
              <li>Event ist nicht mehr öffentlich sichtbar</li>
              <li>Änderungen sind nicht mehr möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeArchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6"
            color="#fb6340"
            @click="confirmArchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Jetzt archivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unarchive Confirmation Dialog -->
    <v-dialog v-model="dialogUnarchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-success me-2" style="font-size: 28px;">unarchive</i>
          Event dearchivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Möchten Sie dieses Event wirklich dearchivieren?
          </p>
          
          <v-alert type="info" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Nach der Dearchivierung:</p>
            <ul class="pl-4 text-typo">
              <li>Event bleibt zunächst inaktiv</li>
              <li>Event und Anmeldungen müssen manuell aktiviert werden</li>
              <li>Bearbeitung ist wieder möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeUnarchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6"
            color="#2dce89"
            @click="confirmUnarchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Dearchivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDelete" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-error me-2" style="font-size: 28px;">delete</i>
          Event endgültig löschen?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Sind Sie sicher, dass Sie dieses Event <strong>endgültig löschen</strong> möchten?
          </p>
          
          <v-alert type="error" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Diese Aktion kann nicht rückgängig gemacht werden!</p>
            <ul class="pl-4 text-typo">
              <li>Das Event wird vollständig aus der Datenbank entfernt</li>
              <li>Alle zugehörigen Daten gehen unwiderruflich verloren</li>
              <li>Das Event kann nicht wiederhergestellt werden</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeDeleteDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-error py-2 px-6"
            color="#f5365c"
            @click="confirmDelete"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">delete</i>
            Endgültig löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import { VueEditor } from "vue2-editor";

import DatePicker from "v-calendar/lib/components/date-picker.umd";
import Dropzone from "./Widgets/Dropzone.vue";
import coreData from "@/mixins/coreDataMixin";
import administrationData from "@/mixins/administrationMixin";
import permissionMixin from "@/mixins/permissionMixin";
import PermissionDenied from "@/components/PermissionDenied.vue";
import DatatableSearch from "./Widgets/DatatableSearch.vue";
import DatatablePushNotifications from "./Widgets/DatatablePushNotifications.vue";
import DeleteDialog from "@/components/DeleteDialog.vue";
import DatatableInvoices from "./Widgets/DatatableInvoices.vue";
import axiosAuth from "@/shared/configs/axios-auth";
export default {
  name: "EventsDetail",
  mixins: [coreData, administrationData, permissionMixin],
  components: {
    DeleteDialog,
    VueEditor,
    DatePicker,
    Dropzone,
    DatatableSearch,
    DatatablePushNotifications,
    DatatableInvoices,
    PermissionDenied,
  },
  data: function () {
    return {
      dialogDelete: false,
      dialogDeleteComment: false,
      dialogArchive: false,
      dialogUnarchive: false,
      editedItemDelete: {},
      imageError: false,
    };
  },
  watch: {
    event() {
      if (this.event) {
        this.getEventComments(this.selectedClientEvent._id);
      }
    },
    selectedClientEvent() {
      // Reset image error when event changes
      this.imageError = false;
    },
    'selectedClientEvent.img'() {
      // Reset error state when image changes
      this.imageError = false;
    },
    $route: function (to, from) {
      console.log("changed event");
    },
  },
  mounted() {
    this.$store.dispatch("getClientEventById", { id: this.$route.params.id });
  },
  computed: {
    canManageEvents() {
      const currentForManagerId = this.$store.getters.currentForManagerId;
      const canManage = currentForManagerId && this.$store.getters.canManageEvents(currentForManagerId);
      console.log('🔍 [canManageEvents]', {
        currentForManagerId,
        canManage
      });
      return canManage;
    },
    isEventDeletable() {
      if (!this.selectedClientEvent) {
        console.log('🔍 [isEventDeletable] No selectedClientEvent');
        return false;
      }
      
      console.log('🔍 [isEventDeletable] Checking event:', {
        _id: this.selectedClientEvent._id,
        active: this.selectedClientEvent.active,
        archived: this.selectedClientEvent.archived,
        entries: this.selectedClientEvent.entries,
        entriesCount: this.selectedClientEvent.entriesCount,
        entriesLength: this.selectedClientEvent.entries?.length,
        canManageEvents: this.canManageEvents
      });
      
      // Event muss inaktiv sein
      if (this.selectedClientEvent.active) {
        console.log('❌ [isEventDeletable] Event is active');
        return false;
      }
      
      // Keine Anmeldungen - prüfe sowohl entries.length als auch entriesCount
      const entriesCount = this.selectedClientEvent.entriesCount !== undefined 
        ? this.selectedClientEvent.entriesCount 
        : (this.selectedClientEvent.entries?.length || 0);
      
      console.log('🔍 [isEventDeletable] Entries count:', entriesCount);
      
      if (entriesCount > 0) {
        console.log('❌ [isEventDeletable] Event has entries');
        return false;
      }
      
      // Keine Zahlungen (wenn entries existieren, aber keine payments)
      // Da entries.length === 0, gibt es auch keine payments
      console.log('✅ [isEventDeletable] Event is deletable!');
      return true;
    },
  },
  methods: {
    handleImageError() {
      this.imageError = true;
    },
    updateEvent() {
      let data = {
        _id: this.selectedClientEvent._id,
        active: this.selectedClientEvent.active,
        entries_active: this.selectedClientEvent.entries_active,
      };
      this.$store.dispatch("updateEvent", data);
      // this.eventEditModal = false;
      this.SnackbarShow("Success", "Event aktualisiert!");
    },
    showDeleteDialog(item) {
      this.editedItemDelete = item;
      this.dialogDelete = true;
    },

    showDeleteCommentDialog(item) {
      this.editedCommentDelete = item;
      this.dialogDelete = true;
    },

    deleteCommentConfirm() {
      this.deleteComment();
    },

    closeCommentDelete() {
      this.editedItemDelete = null;
      this.dialogDelete = false;
    },

    deleteComment() {
      if (!this.editedItemDelete) return;
      axiosAuth
        .delete("eventComments/" + this.editedItemDelete._id)
        .then(() => {
          this.uploadComplete = true;
          this.$store.commit("updating", false);
          this.$store.dispatch(
            "getEventComments",
            this.selectedClientEvent._id
          );
          this.SnackbarShow(
            "Success",
            "Aktion erfolgreich!",
            "Kommentar erfolgreich gelöscht!"
          );
          this.closeCommentDelete();
        })
        .catch((error) => {
          console.log(error);
          this.$store.commit("updating", false);
          this.SnackbarShow("Danger", "Fehler!");
        });
    },

    archiveEvent() {
      this.dialogArchive = true;
    },

    async confirmArchive() {
      this.dialogArchive = false;

      try {
        this.$store.commit("updating", true);

        // PATCH Request mit archived: true (Backend setzt automatisch active: false und entries_active: false)
        await axiosAuth.patch(`/events/${this.selectedClientEvent._id}`, {
          archived: true
        });

        this.SnackbarShow("Success", "Event wurde archiviert und deaktiviert!");
        this.$store.dispatch("getClientEventById", { id: this.$route.params.id });
      } catch (error) {
        console.error('Archive event error:', error);
        this.SnackbarShow("Danger", "Fehler beim Archivieren!");
      } finally {
        this.$store.commit("updating", false);
      }
    },

    closeArchiveDialog() {
      this.dialogArchive = false;
    },

    unarchiveEvent() {
      this.dialogUnarchive = true;
    },

    async confirmUnarchive() {
      this.dialogUnarchive = false;

      try {
        this.$store.commit("updatingClientEvents", true);

        // PATCH Request mit archived: false
        await axiosAuth.patch(`/events/${this.selectedClientEvent._id}`, {
          archived: false,
          archivedAt: null
        });

        this.SnackbarShow("Success", "Event dearchiviert!", "Das Event ist nun wieder bearbeitbar.");
        
        // Event neu laden
        await this.$store.dispatch("getClientEvents", this.selectedClient);
      } catch (error) {
        console.error('Unarchive event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Dearchivieren!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updatingClientEvents", false);
      }
    },

    closeUnarchiveDialog() {
      this.dialogUnarchive = false;
    },

    deleteEvent() {
      this.dialogDelete = true;
    },

    closeDeleteDialog() {
      this.dialogDelete = false;
    },

    async confirmDelete() {
      this.dialogDelete = false;

      try {
        this.$store.commit("updating", true);

        // DELETE Request - Backend prüft ob Event löschbar ist
        await axiosAuth.delete(`/events/${this.selectedClientEvent._id}`);

        this.SnackbarShow("Success", "Event wurde gelöscht!", "Das Event wurde vollständig aus der Datenbank entfernt.");
        
        // Zurück zur Events-Liste
        this.$router.push('/admin/administration').catch(() => {});
      } catch (error) {
        console.error('Delete event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Löschen!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },
  },
};
</script>
